import ElementLoader from "@/Components/Media/ElementLoader"
import CameraDetail from "@/Pages/CameraDetail/CameraDetail"
import type { IWebCOOSAssetSummaryView, IWebCOOSElement, IWebCOOSParsedAsset, IWebCOOSParsedGalleryService } from "@/services/assets/types"
import headerStateAtom from "@/state/headerStateAtom"
import { formatInTimeZone } from "date-fns-tz"
import { useAtom } from "jotai"
import { memo, useEffect, useRef, useState, type ReactElement } from "react"
import { Link, useParams } from "react-router"
import { ChevronRightIcon, ExclamationIcon } from '@heroicons/react/solid';
import { Button, Table, Tabs, Tooltip, ViewWithLoader } from "@axdspub/axiom-ui-utilities"
import { useTimeSeriesAssetBrowse } from "@/services/media/useTimeSeriesAssetBrowse"
import { useAPIContext } from "@/state/ApiContext"
import ServiceLoader from "@/Components/Media/ServiceLoader"
import ImageLoader, { AnnotatedImageLoader } from "@/Components/Media/ImageLoader"
import { ChevronFirst, ChevronLast, ChevronLeftIcon, GridIcon, X } from "lucide-react"
import { makeUTCDate } from "@/services/assets/parsers"
import MediaThumb from "@/Components/Media/MediaThumb"
import PageTitle from "@/Components/PageTitle"
import isMobileAtom from "@/state/isMobileAtom"


/* const NearbyElementGroup = ({
    asset,
    date,
    withinHours
}: {
    asset: IWebCOOSAssetSummaryView,
    date: Date,
    withinHours?: number
}):ReactEement => {
    return <></>
} */


const MessageDisplay = ({ message, asset, service, date }: { message: string, asset: IWebCOOSAssetSummaryView, service: IWebCOOSParsedGalleryService, date: Date }): ReactElement => {
    const timezoneForDisplay = asset.asset_timezone
        ?? Intl.DateTimeFormat().resolvedOptions().timeZone
        ?? 'America/New_York';
    const dtFormat = (date: Date, fmt?: string) => formatInTimeZone(date, timezoneForDisplay, fmt || 'yyyy-MM-dd HH:mm:ss zzz');
    const dateString = dtFormat(date);
    return (
        <div className='text-sm flex flex-col gap-2 text-slate-500 h-full bg-slate-100 text-left p-4 font-normal e'>
            <div className='flex flex-row items-top gap-2'><ExclamationIcon className='w-4 h-4 flex-none mt-1' /> {message}</div>
            <div className='pl-6 flex flex-col gap-4'>
                <p className='text-xs text-slate-500'>{dateString}</p>
                <Link to={`/cameras/${asset.asset_slug}/?service=${service.uuid}}`}
                    className='text-primary hover:underline text-lg'
                >View gallery</Link>
            </div>
        </div>

    )
}


const NearbyElement = ({
    asset,
    service,
    date
}: {
    asset: IWebCOOSAssetSummaryView,
    service: IWebCOOSParsedGalleryService,
    date: Date
}): ReactElement => {

    const apiContext = useAPIContext();
    const { data, isLoading, error } = useTimeSeriesAssetBrowse({
        ...apiContext,
        date: date,
        serviceIdentifier: service.uuid,
        direction: 'nearest',
        enabled: true,
        withinHours: 12,
        count: 1,
        nameOrSlugPrefix: service.common.label.includes('with-shoreline') ? 'annotated.shoreline' : undefined
    })

    return <div>
        <Link
            to={`/cameras/${asset.asset_slug}/?service=${service.uuid}&date=${date.toISOString()}`}
            className='text-primary hover:underline font-semibold text-lg'
        >
            {service.label}
        </Link>
        <div className='aspect-video w-full my-2 flex items-center justify-center relative'>
            <ViewWithLoader data={data} isLoading={isLoading} error={error}>
                {data !== null && data !== undefined &&
                    (
                        'message' in data && typeof data.message === 'string'
                            ? <MessageDisplay message={data.message} asset={asset} service={service} date={date} />
                            : <Link to={`/cameras/${asset.asset_slug}/service/${service.uuid}/media/${(data as IWebCOOSElement).uuid}`} className='block w-full h-full'>
                                <MediaThumb
                                    element={data as IWebCOOSElement}
                                    serviceSlug={service.common.slug}
                                    asset={asset}
                                    defaultDateDisplayClassName='flex flex-row items-center gap-1 text-sm mt-2 text-primary hover:underline'
                                />
                            </Link>
                    )

                }
            </ViewWithLoader>

        </div>
    </div>

}

const NearbyGalleries = ({
    service,
    detail,
    summary,
    date,
    dateString

}: {
    service: IWebCOOSParsedGalleryService,
    detail: IWebCOOSParsedAsset,
    summary: IWebCOOSAssetSummaryView,
    date: Date | undefined,
    dateString: string | undefined

}): ReactElement => {

    const urlParams = new URL(window.location.href).searchParams;
    const [showOtherGalleries, setShowOtherGalleries] = useState<boolean>(urlParams.get('g') === 'true')

    useEffect(() => {
        const url = new URL(window.location.href)
        const urlParams = url.searchParams
        if (showOtherGalleries) {
            urlParams.set('g', 'true')
        } else {
            urlParams.delete('g')
        }
        window.history.replaceState({}, '', url.toString())
    }, [showOtherGalleries])

    return (
        <>
            <Tooltip content={`Show media from other galleries closest to ${dateString}`} dark={true} useSpan={true}>
                <Button
                    className='bg-primary text-white'
                    size='xs'
                    onClick={() => {
                        setShowOtherGalleries(!showOtherGalleries)
                    }}
                ><GridIcon className='h-6 w-6 inline' /> <span className='text-sm pt-2 pl-1'>Show other galleries</span></Button>
            </Tooltip>

            {
                showOtherGalleries && <div className='absolute top-0 right-0 bottom-0 bg-white border border-gray-300  shadow-lg z-31 w-[400px] h-full overflow-y-auto pb-8'>
                    <X className='h-6 w-6 text-slate-400 hover:text-slate-600 cursor-pointer absolute top-4 right-4' onClick={() => { setShowOtherGalleries(false) }} />
                    <h3 className='text-sm text-slate-600 border-b-2 border-b-slate-200 p-4'>Other Galleries</h3>
                    <div className='p-4 flex flex-col gap-12'>
                        {date !== undefined &&
                            detail.galleryServices.filter(s => s.uuid !== service.uuid).map(s => {
                                return <NearbyElement date={date} asset={summary} service={s} key={s.uuid} />
                            })
                        }
                    </div>
                </div>
            }



        </>
    )

}

const onTimeUpdateEventHandler = (e: Event) => {
    const url = new URL(window.location.href);
    const t = Math.floor(((e.target as HTMLVideoElement).currentTime) / 2) * 2;
    const currentT = url.searchParams.get('t');
    if (currentT === null || t !== +currentT) {
        url.searchParams.set('t', String(t));
        window.history.replaceState(null, '', url.toString());
    }
}

const VideoPlayerBase = (props: { src: string, controls?: boolean, className?: string, autoPlay?: boolean, muted?: boolean, onTimeUpdate?: (e: Event) => void }) => {
    const containerElementRef = useRef<HTMLDivElement | null>(null);
    const videoElementRef = useRef<HTMLVideoElement | null>(null);
    const sourceElementRef = useRef<HTMLSourceElement | null>(null);

    const { src, onTimeUpdate, ...videoProps } = props;

    // Informed by:
    //  https://towardsdev.com/when-react-meets-the-dom-directly-a-war-story-about-video-js-4dd5f9d35752
    useEffect(() => {
        const videoElement = document.createElement('video');
        const sourceElement = document.createElement('source')

        videoElementRef.current = videoElement
        sourceElementRef.current = sourceElement

        Object.assign(videoElement, { ...videoProps });
        Object.assign(sourceElement, { src });

        // Handling the "non-value" attributes
        videoElement.controls = videoProps.controls ?? true
        videoElement.autoplay = videoProps.autoPlay ?? true
        videoElement.muted = videoProps.muted ?? true

        if (onTimeUpdate) {
            videoElement.addEventListener("timeupdate", onTimeUpdateEventHandler)
        }

        videoElement.appendChild(sourceElement);
        containerElementRef.current?.appendChild(videoElement);

        return () => {
            // console.log('pausing and removing video')
            videoElementRef.current?.pause();
            if (sourceElementRef.current) {
                sourceElementRef.current.remove();
            }
            videoElementRef.current?.load();
            videoElementRef.current?.remove();
        }
    }, [])

    useEffect(() => {
        if (!sourceElementRef.current) return;
        if (!videoElementRef.current) return;

        videoElementRef.current.pause();

        sourceElementRef.current.src = src;
        videoElementRef.current.load();

    }, [src]);

    return <div ref={containerElementRef} />;
}

const VideoPlayer = memo(VideoPlayerBase)


const ElementDisplay = ({
    element,
    tab,
    setTab
}: {
    element: IWebCOOSElement,
    tab?: string,
    setTab: (tab: string | undefined) => void
}): ReactElement => {
    const imageClassName = 'mx-auto max-w-full object-contain'
    const imageErrorClassName = 'aspect-5/3 w-full'

    return (
        <>
            {
                element.data.type === 'ImageFile'
                    ? <ImageLoader
                        url={element.data.properties.url}
                        className={imageClassName}
                        errorClassName={imageErrorClassName}

                    />
                    : element.data.type === 'VideoFile'
                        ? <VideoPlayer
                            controls={true}
                            className='mx-auto w-full h-full aspect-video'
                            autoPlay={true}
                            muted={true}
                            src={element.data.properties.url}
                            onTimeUpdate={onTimeUpdateEventHandler}
                        />
                        : element.data?.content?.[0]?.annotated_image_reference !== undefined
                            ? <Tabs
                                onChange={setTab}
                                selectedTab={tab}
                                tabs={[
                                    {
                                        id: 'media',
                                        label: 'Media',
                                        content: <AnnotatedImageLoader
                                            annotated_image_reference={element.data.content[0].annotated_image_reference}
                                            imageClassName={imageClassName}
                                            imageErrorClassName={imageErrorClassName}
                                        />
                                    },
                                    {
                                        id: 'details',
                                        label: 'Classification results',
                                        content: <div className='flex flex-col gap-10'>
                                            <Table
                                                className='w-full text-xs'
                                                rowClassName='even:bg-slate-50 odd:bg-slate-100'
                                                columns={
                                                    [
                                                        {
                                                            label: 'Property',
                                                            id: 'label',
                                                            headerClassName: 'text-left'
                                                        },
                                                        {
                                                            label: 'Value',
                                                            id: 'value',
                                                            headerClassName: 'text-left'
                                                        }
                                                    ]

                                                }
                                                data={[
                                                    {
                                                        label: 'Model framework',
                                                        value: element.data?.content?.[0]?.classification_result.classification_model_framework || 'N/A'
                                                    },
                                                    {
                                                        label: 'Model name',
                                                        value: element.data?.content?.[0]?.classification_result.classification_model_name || 'N/A'
                                                    },
                                                    {
                                                        label: 'Model version',
                                                        value: element.data?.content?.[0]?.classification_result.classification_model_version || 'N/A'
                                                    }
                                                ]}
                                            />
                                            <Table
                                                className='w-full text-xs'
                                                rowClassName='even:bg-slate-50 odd:bg-slate-100'
                                                columns={
                                                    [
                                                        {
                                                            label: 'Class',
                                                            id: 'label',
                                                            headerClassName: 'text-left'
                                                        },
                                                        {
                                                            label: 'Confidence',
                                                            id: 'value',
                                                            headerClassName: 'text-left'
                                                        }
                                                    ]
                                                }
                                                data={
                                                    (element.data?.content?.[0]?.classification_result.classification_scores ?? []).map((scores) => {
                                                        return Object.keys(scores).map(key => {
                                                            return {
                                                                label: key,
                                                                value: `${((scores[key] ?? 0) * 100).toFixed(2)}%`
                                                            }
                                                        })
                                                    })
                                                        .flat()
                                                }
                                            />


                                        </div>
                                    }
                                ]}


                            />
                            : <p>Unsupported type: {element.data.type}</p>
            }
        </>
    )
}



const CameraMediaDetail = ({
    service,
    asset_slug,
    element_uuid,
}: {
    service: IWebCOOSParsedGalleryService,
    asset_slug: string,
    element_uuid: string
}): ReactElement => {

    const service_uuid = service.uuid


    const startString = service?.elements.first_starting ?? service?.elements.first_ending ?? null
    const endString = service?.elements.last_ending ?? service?.elements.last_starting ?? null
    const start = startString !== null ? makeUTCDate(startString) : undefined
    const end = endString !== null ? makeUTCDate(endString) : undefined

    const [currentDate, setCurrentDate] = useState<Date | undefined>(undefined)
    const [direction, setDirection] = useState<undefined | 'previous' | 'next' | 'nearest' | 'first' | 'last'>(undefined)
    const [date, setDate] = useState<Date | undefined>(undefined)
    const [currentElementUUID, setCurrentElementUUID] = useState<string | undefined>(element_uuid)





    const apiContext = useAPIContext()

    const needsData = currentDate !== undefined || direction === 'first' || direction === 'last'
    const { data, isLoading, error } = useTimeSeriesAssetBrowse({
        ...apiContext,
        date: currentDate ?? new Date(),
        serviceIdentifier: service_uuid,
        direction,
        enabled: needsData,
        count: 1,
        nameOrSlugPrefix: service.common.label.includes('with-shoreline') ? 'annotated.shoreline' : undefined
    })

    useEffect(() => {
        if (data !== undefined && data !== null && (data as IWebCOOSElement)?.uuid !== currentElementUUID) {
            setCurrentElementUUID((data as IWebCOOSElement)?.uuid)
        }
    }, [data, currentElementUUID])





    return <CameraDetail
        slug={asset_slug}
        View={({ summary, detail }: { summary: IWebCOOSAssetSummaryView, detail: IWebCOOSParsedAsset }): ReactElement => {
            const timezoneForDisplay = summary.asset_timezone
                ?? Intl.DateTimeFormat().resolvedOptions().timeZone
                ?? 'America/New_York';

            const dtFormat = (date: Date, fmt?: string) => formatInTimeZone(date, timezoneForDisplay, fmt || 'yyyy-MM-dd HH:mm:ss zzz');
            const [isMobile] = useAtom(isMobileAtom)
            const serviceLink = <Link to={`/cameras/${summary.asset_slug}?service=${service_uuid}&date=${date?.toISOString()}`} className='text-primary hover:underline'>{service.label}</Link>
            const firstButton = start !== undefined && <Tooltip content="First" dark={true} useSpan={true}>
                <Button
                    className="bg-primary text-white"
                    size='xs'
                    disabled={date === undefined || date.getTime() === +start}
                    onClick={() => {
                        setDirection('first')

                    }}
                ><ChevronFirst className='h-6 w-6  inline' /></Button>
            </Tooltip>
            const prevButton = start !== undefined && <Tooltip content="Previous" dark={true} useSpan={true}>
                <Button
                    className="bg-primary text-white"
                    size='xs'
                    disabled={date === undefined || date.getTime() === +start}
                    onClick={() => {
                        setCurrentDate(date)
                        setDirection('previous')
                    }}
                ><ChevronLeftIcon className='h-6 w-6  inline' /></Button>
            </Tooltip>
            const nextButton = end !== undefined && <Tooltip content="Next" dark={true} useSpan={true}>
                <Button
                    className="bg-primary text-white"
                    size='xs'
                    disabled={date === undefined || date.getTime() === +end}
                    onClick={() => {
                        setCurrentDate(date)
                        setDirection('next')
                    }}
                ><ChevronRightIcon className='h-6 w-6  inline' /></Button>
            </Tooltip>
            const lastButton = end !== undefined && <Tooltip content="Last" dark={true} useSpan={true}>
                <Button
                    className="bg-primary text-white"
                    size='xs'
                    disabled={date === undefined || date.getTime() === +end}
                    onClick={() => {
                        setDirection('last')
                    }}
                ><ChevronLast className='h-6 w-6  inline' /></Button>
            </Tooltip>
            const nearbyGalleries = <NearbyGalleries service={service} detail={detail} summary={summary} date={date} dateString={date ? dtFormat(date) : undefined} />
            return <>{start !== undefined && end !== undefined && <>
                <PageTitle>{`${summary.asset_label} | ${service.label}${date !== undefined ? ` | ${dtFormat(date)}` : ''}`}</PageTitle>
                <div className='p-10'>
                    <div className='flex flex-col gap-2 py-4'>
                        <h1 className='md:text-lg font-bold sticky top-11 bg-white z-20'>
                            <Link to='/cameras' className='text-primary hover:underline'>Cameras</Link>
                            <ChevronRightIcon className='h-6 w-6 text-slate-400 inline mx-2' />
                            <Link to={`/cameras/${summary.asset_slug}`} className='text-primary hover:underline'>{summary.asset_label}</Link>

                        </h1>
                        {
                            !isMobile ?

                                <h2 className='text-2xl font-semibold flex flex-row gap-4 items-center'>
                                    {serviceLink}
                                    <div className='flex flex-row gap-2 items-center'>
                                        {firstButton}
                                        {prevButton}
                                        <span>{date !== undefined ? dtFormat(date) : 'Loading'}</span>
                                        {nextButton}
                                        {lastButton}
                                        {
                                            detail.galleryServices.length > 1 && nearbyGalleries
                                        }

                                    </div>
                                </h2>
                                : <div className='flex flex-col gap-2'>
                                    <strong>{serviceLink}</strong>

                                    <div className='flex flex-row gap-2 items-center'>
                                        {firstButton}
                                        {prevButton}
                                        {nextButton}
                                        {lastButton}
                                    </div>
                                    <div><span>{date !== undefined ? dtFormat(date) : 'Loading'}</span></div>
                                    {
                                        detail.galleryServices.length > 1 &&
                                        <div className='mt-4'>
                                            {nearbyGalleries}
                                        </div>

                                    }

                                </div>
                        }
                    </div>
                    <div className='relative min-h-[50vh]'>
                        <ViewWithLoader data={data ?? element_uuid} isLoading={isLoading} error={error} keepExistingContentWhileLoading={true}>
                            {
                                currentElementUUID !== undefined &&
                                <ElementLoader
                                    elementIdentifier={currentElementUUID}
                                    View={({ element }: { element: IWebCOOSElement }): ReactElement => {
                                        const urlParams = new URL(window.location.href).searchParams;
                                        const [tab, setTab] = useState<string | undefined>(urlParams.get('tab') ?? 'media')
                                        useEffect(() => {
                                            const url = new URL(window.location.href)
                                            const urlParams = url.searchParams
                                            if (tab !== null && tab !== undefined) {
                                                urlParams.set('tab', tab)
                                            } else {
                                                urlParams.delete('tab')
                                            }
                                            window.history.replaceState({}, '', url.toString())
                                        }, [tab])

                                        useEffect(() => {
                                            if (element !== undefined) {
                                                const elementDate = new Date(element.data.extents.temporal.min)
                                                if (elementDate.toISOString() !== date?.toISOString()) {
                                                    setDate(elementDate)
                                                    const urlParams = new URL(window.location.href).searchParams;
                                                    const newUrl = `/cameras/${asset_slug}/service/${service_uuid}/media/${element.uuid}?${urlParams.toString()}`
                                                    if (newUrl !== window.location.pathname + window.location.search) {
                                                        window.history.pushState({}, '', newUrl)
                                                    }
                                                }
                                            }
                                        }, [element])

                                        return <ElementDisplay element={element} tab={tab} setTab={setTab} />
                                    }}

                                />
                            }
                        </ViewWithLoader>

                    </div>

                </div>
            </>
            }
            </>
        }
        }
    />
}

export default (): ReactElement => {

    const [headerState, setHeaderState] = useAtom(headerStateAtom)
    const { asset_slug, service_uuid, element_uuid } = useParams<{ asset_slug: string, service_uuid: string, element_uuid: string }>()

    if (service_uuid === undefined) {
        return <div>No service UUID provided</div>
    }

    useEffect(() => {
        const prevHeaderState = headerState
        setHeaderState(true)
        return () => {
            setHeaderState(prevHeaderState)
        }
    }, [setHeaderState])

    if (asset_slug === undefined) {
        return <div>No asset slug provided</div>
    }
    if (element_uuid === undefined) {
        return <div>No element UUID provided</div>
    }

    return <ServiceLoader serviceIdentifier={service_uuid} View={({ service }: { service: IWebCOOSParsedGalleryService }): ReactElement => {
        return <CameraMediaDetail service={service} asset_slug={asset_slug} element_uuid={element_uuid} />
    }}
    />
}